import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '@/app/providers/globalState';
import { TaskDrawerHeaderContext } from '@/features/tasks/components/Task/taskDrawerContext';
import { TrainFromSpecificUsecase } from '@/pages/TrainFromSpecificUsecase';
import { distillation_in_progress } from '@/shared/constants/taskStatuses';
import WizardStepHeading from './WizardStepHeading';

/**
 * Wizard Step 5 — "Train" (Figma 3001-68512 / 3121-6533).
 * Hosts the existing training surface (Model Summary + Advanced options).
 * The embedded screen registers its "Let's Train" action through
 * TaskDrawerHeaderContext; we render it top-right next to Back per Figma.
 * When training kicks off (usecase status flips to "Distillation in
 * Progress") the wizard finishes straight to the Task Page, where the
 * "Your Model will be processed now" modal (Figma 3001-68567) opens
 * centered over the Data Overview tab.
 */
const TrainStep = ({ activeUsecaseId, taskName, moveToScreen, onBack, onFinished }) => {
    const { allUsecases } = useContext(GlobalContext);
    const [headerNext, setHeaderNext] = useState(null);
    const notifiedRef = useRef(false);

    const activeUsecase = allUsecases.find((u) => u._id === activeUsecaseId) || null;

    // Training started — the train screen sets the status optimistically as
    // soon as the distillation event is created.
    useEffect(() => {
        if (
            activeUsecase?.status === distillation_in_progress &&
            !notifiedRef.current
        ) {
            notifiedRef.current = true;
            onFinished();
        }
    }, [activeUsecase?.status, onFinished]);

    if (!activeUsecaseId) return null;

    return (
        <div className="cw-step cw-step--hosted">
            <WizardStepHeading
                title="Training"
                taskName={taskName}
                onBack={onBack}
                onNext={headerNext?.onClick ?? (() => {})}
                nextLabel={headerNext?.label ?? "Let's Train"}
                nextDisabled={!headerNext || !!headerNext.disabled}
                nextLoading={!!headerNext?.loading}
                nextLoadingLabel={headerNext?.loadingLabel ?? 'Training…'}
                nextDisableWhenReadOnly
                errorMessage={headerNext?.errorMessage || null}
            />

            <TaskDrawerHeaderContext.Provider value={setHeaderNext}>
                <TrainFromSpecificUsecase
                    key={activeUsecaseId}
                    embeddedInTaskDrawer
                    moveToScreen={moveToScreen}
                />
            </TaskDrawerHeaderContext.Provider>
        </div>
    );
};

export default TrainStep;
